import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Subject, debounceTime, first, startWith, takeUntil } from 'rxjs';
import { CreateUserDialogComponent } from 'src/app/components/dashboard/roles/create-user-dialog/create-user-dialog.component';
import {
  ListClientItem,
  ListClientsResponse,
} from 'src/app/services/clients/clients.service';
import {
  UserDataTableItem,
  UsersService,
} from 'src/app/services/users/users.service';
import { DeleteUserDialogComponent } from './delete-user-dialog/delete-user-dialog.component';
import { EditUserDialogComponent } from './edit-user-dialog/edit-user-dialog.component';
import { EmailFilterComponent } from './email-filter/email-filter.component';
import { NameFilterComponent } from './name-filter/name-filter.component';
import { RoleFilterComponent } from './role-filter/role-filter.component';
import { ClientFilterComponent } from './client-filter/client-filter.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
  imports: [
    EmailFilterComponent,
    NameFilterComponent,
    RoleFilterComponent,
    ClientFilterComponent,
    PayeeButtonComponent,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe,
  ],
})
export class RolesComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private dialog = inject(MatDialog);

  private readonly destroy = new Subject<void>();
  readonly setUsers$ = new Subject<void>();

  readonly dataSource = new MatTableDataSource<UserDataTableItem>();

  clientOptions: ListClientsResponse['clients'] = [];
  tableLoading = false;
  updateLoading = false;

  selectedEmail: string | null = null;
  selectedUserName: string | null = null;
  selectedRole: { name: string } | null = null;
  selectedClient: ListClientItem | null = null;

  get hasActiveFilters(): boolean {
    return !!(
      this.selectedEmail ||
      this.selectedUserName ||
      this.selectedRole ||
      this.selectedClient
    );
  }
  readonly displayedColumns = [
    'created_at',
    'email',
    'user_name',
    'role_name',
    'clients',
    'digest_notifications',
    'edit_roles',
    'delete',
  ] as const;

  pageSize = 10;
  pageIndex = 0;
  length = 0;

  sort_by?: keyof UserDataTableItem;
  sort_direction: 'asc' | 'desc' = 'asc';

  ngOnInit(): void {
    this.setUsers$
      .pipe(takeUntil(this.destroy), debounceTime(500), startWith(null))
      .subscribe(async () => await this.setUsers());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async pageChange(event: PageEvent) {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
    }
    if (event.pageIndex !== this.pageIndex) {
      this.pageIndex = event.pageIndex;
    }
    this.setUsers$.next();
  }

  async sortData(event: Sort): Promise<void> {
    this.sort_by = event.active as keyof UserDataTableItem;
    this.sort_direction = event.direction || 'asc';

    this.setUsers$.next();
  }

  async toggleDigestNotifications(
    element: UserDataTableItem,
    change: MatCheckboxChange,
  ): Promise<void> {
    const checked = change.checked;
    element.digest_notifications = checked;
    try {
      this.updateLoading = true;

      const result = await this.usersService.updateUser(element.user_uuid, {
        digest_notifications: checked,
      });

      element.digest_notifications = result.digest_notifications;
    } catch (error: unknown) {
      console.error('Error while updating digest notifications', error);

      element.digest_notifications = !checked;
    } finally {
      this.updateLoading = false;
    }
  }

  async sendDigestNotifications(element: UserDataTableItem): Promise<void> {
    try {
      this.updateLoading = true;
      element.digest_notifications_exists = false;

      await this.usersService.sendDigestNotifications(element.user_uuid);
    } catch (error: unknown) {
      console.error('Error while sending digest notifications', error);

      element.digest_notifications_exists = true;
    } finally {
      this.updateLoading = false;
    }
  }

  editUser(user: UserDataTableItem): void {
    const ref = this.dialog.open(EditUserDialogComponent, {
      data: user,
    });
    ref
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: async (updated: boolean) => {
          if (updated) {
            await this.setUsers();
          }
        },
      });
  }

  deleteUser(user: UserDataTableItem): void {
    const ref = this.dialog.open<
      DeleteUserDialogComponent,
      UserDataTableItem,
      boolean | undefined
    >(DeleteUserDialogComponent, {
      data: user,
    });
    ref
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: async (deleted) => {
          if (deleted) {
            await this.setUsers();
          }
        },
      });
  }

  createUser(): void {
    const ref = this.dialog.open(CreateUserDialogComponent);
    ref
      .afterClosed()
      .pipe(first())
      .subscribe({
        next: async (email?: string) => {
          if (email) {
            this.selectedEmail = email;
          }
        },
      });
  }

  clearAllFilters(): void {
    this.selectedEmail = null;
    this.selectedRole = null;
    this.selectedUserName = null;
    this.selectedClient = null;
    this.setUsers$.next();
  }

  private async setUsers() {
    try {
      this.tableLoading = true;
      const res = await this.usersService.getUsersDataTable({
        user_email: this.selectedEmail || undefined,
        user_name: this.selectedUserName || undefined,
        role_name: this.selectedRole?.name || undefined,
        client_id: this.selectedClient?.uuid || undefined,
        page: this.pageIndex + 1,
        per_page: this.pageSize,
        sort_by: this.sort_by,
        sort_direction: this.sort_direction,
      });

      this.length = res.pagination.total;
      this.dataSource.data = res.data;
    } catch (error) {
      console.error('Error while loading users', error);
    } finally {
      this.tableLoading = false;
    }
  }
}
